/**
 * analytics.js — Lightweight, RGPD-friendly first-party tracking.
 *
 * - Session ID: random UUID stored in localStorage with a 30-minute
 *   inactivity TTL. No cookie, no third-party pixel.
 * - Source attribution: read once at first page-view of the session
 *   from `?utm_source=` then `document.referrer`. Persisted with the
 *   session so subsequent events stay attributed to the same source.
 * - Batching: events are queued and flushed on `beforeunload`, every
 *   5 seconds, or immediately when the queue reaches 10 events.
 *
 * Public API:
 *   initAnalytics()               ← call once at app boot
 *   trackPageView(path)           ← call on every route change
 *   track(eventType, meta)        ← generic event
 */
import api from "@/lib/api";

const STORAGE_KEY = "discoveur.analytics.session";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min inactivity
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 10;

let queue = [];
let session = null;
let flushTimer = null;

function _newSessionId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function _detectSource() {
    try {
        const url = new URL(window.location.href);
        const utm = url.searchParams.get("utm_source");
        if (utm) return utm.slice(0, 48).toLowerCase();
        const ref = document.referrer || "";
        if (!ref) return "direct";
        const host = new URL(ref).host.replace(/^www\./, "");
        if (host === window.location.host) return null; // internal nav
        if (/(google|bing|duckduckgo|yahoo|yandex|ecosia)\./.test(host)) return "organic";
        if (/(facebook|instagram|tiktok|twitter|x\.com|linkedin|snapchat|threads|whatsapp|telegram|reddit)\./.test(host))
            return "social";
        return "referrer";
    } catch {
        return "direct";
    }
}

function _loadSession() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const s = JSON.parse(raw);
        if (Date.now() - (s.last || 0) > SESSION_TTL_MS) return null;
        return s;
    } catch {
        return null;
    }
}

function _saveSession(s) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota */ }
}

function _ensureSession() {
    if (session && Date.now() - session.last < SESSION_TTL_MS) {
        session.last = Date.now();
        _saveSession(session);
        return session;
    }
    session = _loadSession();
    if (!session) {
        session = {
            id: _newSessionId(),
            source: _detectSource() || "direct",
            started: Date.now(),
            last: Date.now(),
        };
    } else {
        session.last = Date.now();
    }
    _saveSession(session);
    return session;
}

async function _flush() {
    if (!queue.length) return;
    const batch = queue.splice(0, queue.length);
    // Fire and forget — analytics failures MUST NOT surface to users.
    for (const evt of batch) {
        try {
            await api.post("/analytics/track", evt);
        } catch {
            // Drop on error — the tracker is best-effort by design.
        }
    }
}

export function initAnalytics() {
    _ensureSession();
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = setInterval(_flush, FLUSH_INTERVAL_MS);
    if (typeof window !== "undefined") {
        // Flush on unload — the sync XHR path is prohibited so this is
        // best-effort. Modern browsers keep fetch keepalive for up to
        // a few seconds after unload, which is enough.
        window.addEventListener("beforeunload", () => { _flush(); });
        window.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") _flush();
        });
    }
}

export function track(eventType, meta = {}) {
    if (!eventType) return;
    const s = _ensureSession();
    queue.push({
        session_id: s.id,
        event_type: String(eventType).slice(0, 48),
        path: (typeof window !== "undefined" ? window.location.pathname : ""),
        referrer: (typeof document !== "undefined" ? document.referrer : ""),
        source: s.source,
        meta: meta || {},
    });
    if (queue.length >= FLUSH_BATCH_SIZE) _flush();
}

export function trackPageView(pathOverride) {
    const s = _ensureSession();
    queue.push({
        session_id: s.id,
        event_type: "page_view",
        path: pathOverride || (typeof window !== "undefined" ? window.location.pathname : ""),
        referrer: (typeof document !== "undefined" ? document.referrer : ""),
        source: s.source,
        meta: {},
    });
    if (queue.length >= FLUSH_BATCH_SIZE) _flush();
}
